/**
 * Why this company has the score it has.
 *
 * The stored number ranks a company against 376 000 others and, being one
 * integer, explains nothing. This card asks the server to work the same number
 * out again for this one row and show its parts, which is three cheap queries
 * and no network call - so it loads on its own rather than behind a button, the
 * way the domain and scan cards have to.
 *
 * The two things it must be honest about:
 *
 *   - Absence of evidence is drawn as absence, not as a good result. A company
 *     nobody has scanned says so, next to the rung it fell back to.
 *   - When the recomputed number differs from the stored one, the list the
 *     operator arrived from is out of date. The card says which is which rather
 *     than silently showing a third number.
 */
import { useEffect, useState } from "react";
import { Gauge, Info } from "lucide-react";
import { LoadingState } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { getWebNeed, type WebNeedExplanation, type WebNeedReason } from "../../../../lib/webNeed";
import { fmtDateTime } from "../../../ui";
import { Card } from "../detailCard";
import { WebNeedPill } from "./WebNeedPill";

/** A signed contribution, always with its sign, so a deduction reads as one. */
const signed = (n: number): string => (n > 0 ? `+${n}` : String(n));

function Reasons({ rows, title }: { rows: WebNeedReason[]; title: string }) {
  const t = useT();
  if (rows.length === 0) return null;
  return (
    <div>
      <p className="crm-lab mb-1.5">{title}</p>
      <ul className="space-y-1">
        {rows.map((r) => (
          <li key={`${r.side}-${r.id}`} className="flex items-baseline gap-2 text-[12.5px]">
            <span className="crm-tnum crm-mut w-9 shrink-0 text-right">{signed(r.points)}</span>
            <span>{t(`ariregister.webNeed.reason.${r.id}`)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function WebNeedCard({ regCode }: { regCode: string }) {
  const t = useT();
  const [data, setData] = useState<WebNeedExplanation | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();
    setData(null);
    setFailed(false);
    getWebNeed(regCode, ctrl.signal)
      .then(setData)
      .catch(() => {
        if (!ctrl.signal.aborted) setFailed(true);
      });
    return () => ctrl.abort();
  }, [regCode]);

  if (failed) return null;
  if (!data) {
    return (
      <Card title={t("ariregister.webNeed.title")} icon={Gauge}>
        <LoadingState variant="inline" label={t("common.loading")} />
      </Card>
    );
  }

  const stale = data.storedScore != null && data.storedScore !== data.score;

  return (
    <Card title={t("ariregister.webNeed.title")} icon={Gauge} aside={<WebNeedPill score={data.score} />}>
      <p className="crm-mut mb-4 text-[12.5px]">{t("ariregister.webNeed.intro")}</p>

      <div className="mb-4 grid grid-cols-2 gap-3">
        <div className="rounded-lg bg-[var(--crm-sunk)] px-3 py-2">
          <p className="crm-lab">{t("ariregister.webNeed.gap")}</p>
          <p className="crm-tnum text-[15px] font-semibold">{data.gap}</p>
        </div>
        <div className="rounded-lg bg-[var(--crm-sunk)] px-3 py-2">
          <p className="crm-lab">{t("ariregister.webNeed.reach")}</p>
          <p className="crm-tnum text-[15px] font-semibold">{data.reach}</p>
        </div>
      </div>

      <div className="space-y-4">
        <Reasons rows={data.reasons.filter((r) => r.side === "gap")} title={t("ariregister.webNeed.gapReasons")} />
        <Reasons rows={data.reasons.filter((r) => r.side === "reach")} title={t("ariregister.webNeed.reachReasons")} />
      </div>

      {/* What was NOT available. Shown, never scored: a company nobody has looked
          at must not be presented as a company with nothing wrong. */}
      {data.domain ? (
        <p className="crm-mut mt-4 flex items-start gap-1.5 text-[12px]">
          <Info className="mt-[2px] size-3.5 shrink-0" />
          <span>
            {t("ariregister.webNeed.evidence", {
              domain: data.domain,
              scan: t(data.hasScan ? "ariregister.webNeed.evidenceYes" : "ariregister.webNeed.evidenceNo"),
              dns: t(data.hasDomainCheck ? "ariregister.webNeed.evidenceYes" : "ariregister.webNeed.evidenceNo"),
            })}
          </span>
        </p>
      ) : null}

      <p className="crm-mut mt-3 text-[12px]">
        {stale
          ? t("ariregister.webNeed.stale", { stored: data.storedScore ?? 0, at: fmtDateTime(data.computedAt) })
          : t("ariregister.webNeed.computedAt", { at: fmtDateTime(data.computedAt) })}
      </p>
    </Card>
  );
}
